/**
 * Doomsworn — persistence
 *
 * An in-memory ledger is a demo. Debt, Liens, Callings, the Doom Clock, Wear
 * and rune Grip have to survive a reload and a new session, or the whole claim
 * of the system — that the engine remembers what you spent and cannot be talked
 * out of it — is not true.
 *
 * Storage needs no migration. `machina_characters.data` stores the entire
 * character object verbatim, so this state rides along in `Character.systemState`
 * under the `doomsworn` key, inheriting the character's existing RLS. Core owns
 * that slot generically and knows nothing about what is inside it; this module
 * owns the shape, the validation and the defaults.
 *
 * Everything here is total: a character with no Doomsworn state at all reads
 * back as a valid empty state rather than undefined, so no caller has to guard.
 * Persistence that throws on a missing field is persistence that loses a
 * session, and this is the state a session IS.
 */
import { z } from 'zod';
import { openLedger } from './ledger.js';
import { newDoomState } from './doom.js';
import { RUNE_NAMES } from './runes.js';
import { DOOM_DIE_TRACK } from './doom.js';
import { clampTilt } from './tilt.js';
/** The key this system's state lives under in `Character.systemState`. */
export const DOOMSWORN_STATE_KEY = 'doomsworn';
/**
 * Bump only for a shape change that older rows cannot satisfy. Read is
 * tolerant by design, so most additions need no bump — the version exists so a
 * genuinely breaking change can be detected rather than silently misread.
 */
export const DOOMSWORN_STATE_VERSION = 1;
// ---------------------------------------------------------------------------
// SCHEMA
// ---------------------------------------------------------------------------
const LienSchema = z.object({
    id: z.string(),
    powerId: z.string(),
    claim: z.string(),
    takenAt: z.number().optional(),
    satisfied: z.boolean().default(false),
});
const CallingSchema = z.object({
    id: z.string(),
    powerId: z.string(),
    ask: z.string(),
    issuedAt: z.number().optional(),
    status: z.enum(['open', 'answered', 'refused']).default('open'),
});
const DebtEntrySchema = z.object({
    powerId: z.string(),
    debt: z.number().int().min(0).default(0),
    liens: z.array(LienSchema).default([]),
    callings: z.array(CallingSchema).default([]),
    inverted: z.boolean().optional(),
});
const DoomStateSchema = z.object({
    declared: z.string().default(''),
    die: z.enum(DOOM_DIE_TRACK).default('d4'),
    clock: z.number().int().min(0).default(0),
    arrived: z.boolean().default(false),
});
const WearMarkSchema = z.object({
    id: z.string(),
    source: z.enum(['doom-clock', 'lien', 'grip', 'will-contest', 'calling', 'other']).default('other'),
    description: z.string(),
    takenAt: z.number().optional(),
});
const RuneMarkSchema = z.object({
    id: z.string(),
    rune: z.enum(RUNE_NAMES),
    atGrip: z.number().int().default(0),
    description: z.string(),
});
const RuneHoldingSchema = z.object({
    rune: z.enum(RUNE_NAMES),
    rating: z.number().int().min(0).default(0),
    grip: z.number().int().min(0).default(0),
    marks: z.array(RuneMarkSchema).default([]),
});
export const DoomswornCharacterStateSchema = z.object({
    schemaVersion: z.number().int().default(DOOMSWORN_STATE_VERSION),
    roleId: z.string().optional(),
    /** The way the world tries to cast this character. Read every scene. */
    pull: z.string().optional(),
    /**
     * ATTUNEMENT — legacy slot. NOT the source of truth.
     *
     * This lived here while `AbilityScores` was a closed six-key object. It now
     * has a catchall, so Attunement is an ability like the other six and lives at
     * `Character.abilityScores.attunement` — entered in the creation wizard,
     * shown on the sheet, and translated to the `atn` id the engine reads via
     * `toSystemAbilities()`. One ability cannot have two homes without one of
     * them going stale, and the six-stat block is the home a player looks in.
     *
     * Kept so rows written by an older build still round-trip rather than being
     * silently dropped. Read Attunement through the ability scores.
     *
     * @deprecated Use `Character.abilityScores.attunement`.
     */
    attunement: z.number().int().min(0).default(0),
    doom: DoomStateSchema.default({}),
    /** Per-Power ledger entries. */
    entries: z.array(DebtEntrySchema).default([]),
    /**
     * The ledger's id counter.
     *
     * This MUST round-trip. Lien and Calling ids are derived from it, so a reload
     * that reset it to zero would mint a second `LIEN-kethrem-3` alongside the
     * first and satisfying one would silently satisfy both.
     */
    seq: z.number().int().min(0).default(0),
    wear: z.array(WearMarkSchema).default([]),
    runes: z.array(RuneHoldingSchema).default([]),
    concessions: z.number().int().min(0).default(0),
    concessionLog: z.array(z.string()).default([]),
});
export function emptyState() {
    return DoomswornCharacterStateSchema.parse({});
}
/**
 * Read the persisted state off a character.
 *
 * Tolerant on purpose: absent state, a partially-written row, or a field added
 * since the row was saved all resolve to valid defaults. The one thing that is
 * NOT silently accepted is a future schemaVersion — that means the row was
 * written by a newer build and reading it with these defaults would quietly
 * discard whatever it knows. Better to log loudly and hand back an empty state
 * the caller can refuse to overwrite.
 */
export function readCharacterState(character) {
    const raw = character.systemState?.[DOOMSWORN_STATE_KEY];
    if (raw === undefined || raw === null)
        return emptyState();
    const version = raw.schemaVersion;
    if (typeof version === 'number' && version > DOOMSWORN_STATE_VERSION) {
        console.warn(`[doomsworn] Character ${character.id} has state at schemaVersion ${version}, newer than this build's ${DOOMSWORN_STATE_VERSION}. Refusing to interpret it.`);
        return emptyState();
    }
    const parsed = DoomswornCharacterStateSchema.safeParse(raw);
    if (parsed.success)
        return parsed.data;
    console.warn(`[doomsworn] Character ${character.id} has unreadable state, falling back to empty:`, parsed.error.issues.map(i => `${i.path.join('.')}: ${i.message}`).join('; '));
    return emptyState();
}
/** Hydrate the persisted shape into the live engine objects. */
export function toRuntime(characterId, state) {
    const ledger = openLedger(characterId, state.entries);
    return {
        // openLedger seeds seq from entry count; the persisted counter is the
        // authority and must win, or ids collide after a reload.
        ledger: { ...ledger, seq: state.seq },
        doom: { ...newDoomState(), ...state.doom },
        wear: state.wear,
        runes: state.runes,
        roleId: state.roleId,
        pull: state.pull,
        attunement: state.attunement,
        concessions: state.concessions,
        concessionLog: state.concessionLog,
    };
}
/** Read straight to the live objects. */
export function loadCharacterState(character) {
    return toRuntime(character.id, readCharacterState(character));
}
// ---------------------------------------------------------------------------
// WRITE
// ---------------------------------------------------------------------------
/** Flatten the live engine objects back into the persisted shape. */
export function fromRuntime(runtime) {
    return {
        schemaVersion: DOOMSWORN_STATE_VERSION,
        roleId: runtime.roleId,
        pull: runtime.pull,
        attunement: runtime.attunement,
        doom: runtime.doom,
        entries: runtime.ledger.entries,
        seq: runtime.ledger.seq,
        wear: runtime.wear,
        runes: runtime.runes,
        concessions: runtime.concessions,
        concessionLog: runtime.concessionLog,
    };
}
/**
 * Write state back onto a character, returning a NEW character object.
 *
 * Other systems' slots in `systemState` are preserved untouched — a character
 * that once played something else keeps that record.
 */
export function writeCharacterState(character, state) {
    const persisted = 'ledger' in state ? fromRuntime(state) : state;
    return {
        ...character,
        systemState: {
            ...(character.systemState ?? {}),
            [DOOMSWORN_STATE_KEY]: persisted,
        },
    };
}
// ---------------------------------------------------------------------------
// CAMPAIGN STATE — the Tilt
// ---------------------------------------------------------------------------
/**
 * The Tilt is campaign state, not character state, so it does not ride on the
 * character row. It is engine-owned: the narrator reads it and the UI must not
 * be able to write it (ISS-0132).
 */
export const DoomswornCampaignStateSchema = z.object({
    schemaVersion: z.number().int().default(DOOMSWORN_STATE_VERSION),
    /** Negative is Law, positive is Chaos, poles at ±10. */
    tilt: z.number().int().default(0),
    /** Tick/session stamp of the last engine write. */
    updatedAt: z.number().optional(),
});
export function emptyCampaignState() {
    return DoomswornCampaignStateSchema.parse({});
}
/** Tolerant read, matching the character path. Clamps a stored Tilt into range. */
export function readCampaignState(raw) {
    if (raw === undefined || raw === null)
        return emptyCampaignState();
    const parsed = DoomswornCampaignStateSchema.safeParse(raw);
    if (!parsed.success) {
        console.warn('[doomsworn] Unreadable campaign state, falling back to a held Balance.');
        return emptyCampaignState();
    }
    return { ...parsed.data, tilt: clampTilt(parsed.data.tilt) };
}
//# sourceMappingURL=persistence.js.map